import { Component, Input, OnDestroy } from '@angular/core';
import { GbgrundstueckService, GrundstueckDetailEntryMeta, GrundstueckId, GrundstueckNummerListEntry } from '../../../../core/gb-api';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { ListLoader } from '../../../../shared/components/list-loader/list-loader';
import { LastRechtRootEntryVm, StandardrechtViewmodelService } from '../../../../core/services/standardrecht-viewmodel.service';
import { ListLoaderInfiniteBuilder } from '../../../../shared/components/list-loader/list-loader-infinite';
import { AbstractComponent } from '../../../../shared/components/abstract-component/abstract.component';
import { EigentumanteilMasterDetailVM, EigentumViewmodelService } from '../../../../core/services/eigentum-viewmodel.service';
import { map, tap } from 'rxjs/operators';
import { AppPermissionService } from '../../../../core/services/app-permission.service';
import { KopfdatenelementComponent } from '../../../../shared/widgets/kopfdatenelement/kopfdatenelement.component';
import { KompaktTimepointComponent } from '../../../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { GrundstueckflaecheComponent } from '../../../../shared/widgets/grundstueck/table-grundstueckflaeche/grundstueckflaeche.component';
import { GebaeudeComponent } from '../../../../shared/widgets/grundstueck/table-gebaeude/gebaeude.component';
import { StammgrundstueckComponent } from '../../../../shared/widgets/grundstueck/table-stammgrundstueck/stammgrundstueck.component';
import { SonderrechtComponent } from '../../../../shared/widgets/grundstueck/table-sonderrecht/sonderrecht.component';
import { DominierteGrundstueckeComponent } from '../../../../shared/widgets/grundstueck/table-dominierte-grundstuecke/dominierte-grundstuecke.component';
import { BeschreibungTableWrapperComponent } from '../../../../shared/components/table/beschreibung-table-wrapper.component';
import { StandardrechtTableComponent } from '../../../../shared/widgets/standardrecht-table/standardrecht-table.component';
import { KuhrechtComponent } from '../../../../shared/widgets/grundstueck/table-kuhrecht/kuhrecht.component';
import { AlpgrundstueckeComponent } from '../../../../shared/widgets/grundstueck/table-alpgrundstuecke/alpgrundstuecke.component';
import { EigentumTableComponent } from '../../../../shared/widgets/eigentum-table/eigentum-table.component';
import { GrundstueckBemerkungComponent } from '../../../../shared/widgets/grundstueck/grundstueck-bemerkung/grundstueck-bemerkung.component';
import { DatumPipe } from '../../../../shared/pipes/simple/datum.pipe';
import { TranslateNamePipe } from '../../../../shared/pipes/simple/translate-name.pipe';

@Component({
  selector: 'cap-meldungsrelevante-grundstuecke',
  templateUrl: './meldungsrelevante-grundstuecke.component.html',
  styleUrls: ['./meldungsrelevante-grundstuecke.component.scss'],
  imports: [
    KopfdatenelementComponent,
    KompaktTimepointComponent,
    GrundstueckflaecheComponent,
    GebaeudeComponent,
    StammgrundstueckComponent,
    SonderrechtComponent,
    DominierteGrundstueckeComponent,
    BeschreibungTableWrapperComponent,
    StandardrechtTableComponent,
    KuhrechtComponent,
    AlpgrundstueckeComponent,
    EigentumTableComponent,
    GrundstueckBemerkungComponent,
    TranslatePipe,
    DatumPipe,
    TranslateNamePipe
  ]
})
export class MeldungsrelevanteGrundstueckeComponent extends AbstractComponent implements OnDestroy {
  _grundstueckId: GrundstueckId;
  grundstueck: GrundstueckDetailEntryMeta;
  gsNummerEntry: GrundstueckNummerListEntry;
  gsNummer: string;

  sdrDienstbarkeitLoader: ListLoader<LastRechtRootEntryVm>;
  dienstbarkeitErsterfassungUebergeben: boolean;

  eigentumLoader: ListLoader<EigentumanteilMasterDetailVM>;
  eigentumErsterfassungUebergeben: boolean;

  protected hasPermissionDominierteGrundstuecke: boolean;

  private readonly CHUNK_SIZE = 5;
  private readonly PAGE_SIZE = 5;
  private readonly CURRENT_PAGE = 1;

  constructor(
    private timepointService: TimepointService,
    private grundstueckService: GbgrundstueckService,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private permissionService: AppPermissionService,
    private standardrechtViewmodelService: StandardrechtViewmodelService,
    private eigentumViewmodelSercice: EigentumViewmodelService
  ) {
    super();
  }

  @Input()
  set grundstueckId(value: GrundstueckId) {
    this._grundstueckId = value;
    if (!value) {
      return;
    }
    this.checkPermissionDominierteGrundstuecke();
    this.update();
  }

  get grundstueckId(): GrundstueckId {
    return this._grundstueckId;
  }

  private combineIfNotNull(delimiter: string, nummer: any) {
    if (nummer) {
      return delimiter + nummer.toString();
    }
    return '';
  }

  update() {
    const timepointCtx = this.timepointService.timepointCtx;
    this.addSub(
      this.grundstueckService
        .loadGrundstueckById(
          //
          this._grundstueckId.id, //
          this.appConfigService.einstellungHistorischeDaten, //
          timepointCtx.timepointIso, //
          timepointCtx.timepointId //
        )
        .subscribe((grundstueck) => {
          this.grundstueck = grundstueck;
          this.calculateStringRepresentation();
        })
    );
    this.sdrDienstbarkeitLoader = this.getSdrDienstbarkeitLoader();
    this.eigentumLoader = this.getEigentumLoader();
  }

  private getSdrDienstbarkeitLoader(): ListLoader<LastRechtRootEntryVm> {
    const observable = (offset: number, limit: number) => {
      return this.standardrechtViewmodelService
        .loadGbMeldungsSichtByGrundstueckId(this.grundstueckId, 'SDR_DIENSTBARKEIT', offset, limit, this.subscriptions)
        .pipe(
          tap((res) => (this.dienstbarkeitErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };
    const loader = ListLoaderInfiniteBuilder.builder<LastRechtRootEntryVm>()
      .setLookupItemFunc(observable)
      .setChunkSize(this.CHUNK_SIZE)
      .setPageSize(this.PAGE_SIZE)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPublishItemsWhenAllLoaded(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private getEigentumLoader(): ListLoader<EigentumanteilMasterDetailVM> {
    const observable = (offset: number, limit: number) => {
      return this.eigentumViewmodelSercice
        .loadSichtByGrundstueckIdGbMeldung(this._grundstueckId, false, true, false, offset, limit, this.subscriptions)
        .pipe(
          tap((res) => (this.eigentumErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };

    const loader = ListLoaderInfiniteBuilder.builder<EigentumanteilMasterDetailVM>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .setChunkSize(this.CHUNK_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private calculateStringRepresentation() {
    this.gsNummerEntry = this.grundstueck.grundstueckNummern[this.grundstueck.grundstueckNummern.length - 1];

    this.gsNummer = this.gsNummerEntry.nummer;
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummerZusatz);
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummer3);
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummer4);
    this.gsNummer += this.grundstueck.isKopie ? ' (' + this.translate.instant('GrundstückKopie') + ')' : '';
  }

  checkPermissionDominierteGrundstuecke() {
    this.hasPermissionDominierteGrundstuecke = this.permissionService.hasPermission('ANZEIGE_DOMINIERTE_GRUNDSTUECKE');
  }
}
